import type { ReactTable, Row, RowData } from '@tanstack/react-table';
import { row_getVisibleCells } from '@tanstack/react-table/static-functions';
import type { ReactNode } from 'react';

import { AbsoluteLoader } from '@/components/absoluteLoader';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { t } from '@/i18n';
import { cn } from '@/lib/utils';
import type { DataTableColumnMeta, DataTableFeatures } from './data-table-features';

export type DataTableProps<TFeatures extends DataTableFeatures, TData extends RowData> = {
	/** Table instance created by the caller with `useTable`. */
	table: ReactTable<TFeatures, TData, unknown>;
	isLoading?: boolean;
	/** New rows are loading while the old ones are still shown (e.g. `isPlaceholderData`): overlay a
	 * spinner so the stale rows can't be clicked. */
	isFetching?: boolean;
	/** Rendered in a full-width row when there are no rows. */
	emptyState?: ReactNode;
	onRowClick?: (row: Row<TFeatures, TData>) => void;
	/** Extra classes per body row, e.g. to highlight selected rows. */
	getRowClassName?: (row: Row<TFeatures, TData>) => string | false | undefined;
	className?: string;
	tableClassName?: string;
};

// Static helpers are used instead of e.g. `row.getVisibleCells()` because instance APIs are typed
// per feature set and don't resolve while `TFeatures` is generic.

/**
 * Presentational renderer for a TanStack table instance. State, data fetching and column
 * definitions stay with the caller; per-column styling comes from `DataTableColumnMeta`.
 */
export function DataTable<TFeatures extends DataTableFeatures, TData extends RowData>({
	table,
	isLoading,
	isFetching,
	emptyState,
	onRowClick,
	getRowClassName,
	className,
	tableClassName,
}: DataTableProps<TFeatures, TData>) {
	if (isLoading) {
		return (
			<div className="p-8 text-center flex justify-center">
				<Spinner />
			</div>
		);
	}

	const rows = table.getRowModel().rows;
	const headerGroups = table.getHeaderGroups();
	// Header groups are built from visible columns only, so the last one spans the full width.
	const visibleColumnCount = headerGroups.at(-1)?.headers.length ?? 1;

	return (
		// A container, so columns can drop out by the table's own width (`@3xl:` …) rather than the
		// viewport's: next to the 3D viewer the window is wide but the panel holding the table is not.
		// A container does not size from its content, so it needs a parent that gives it a width:
		// in a shrink-to-fit parent (`w-fit`, inline-flex, a popover) it collapses to 0.
		<div
			className={cn('@container relative bg-card rounded-lg border border-border overflow-hidden', className)}
			aria-busy={isFetching || undefined}
		>
			<AbsoluteLoader isLoading={!!isFetching}>
				<Spinner />
			</AbsoluteLoader>
			<Table className={tableClassName}>
				<TableHeader>
					{headerGroups.map((headerGroup) => (
						<TableRow key={headerGroup.id} className="bg-background hover:bg-background border-border">
							{headerGroup.headers.map((header) => {
								const meta = header.column.columnDef.meta as DataTableColumnMeta | undefined;
								return (
									<TableHead
										key={header.id}
										colSpan={header.colSpan}
										className={cn('px-4 text-muted-foreground font-semibold h-10', meta?.headerClassName)}
									>
										{header.isPlaceholder ? null : <table.FlexRender header={header} />}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{rows.length === 0 && (
						<TableRow>
							<TableCell colSpan={visibleColumnCount} className="text-center py-4">
								{emptyState ?? t('common.states.noResults')}
							</TableCell>
						</TableRow>
					)}
					{rows.map((row) => (
						<TableRow
							key={row.id}
							className={cn(
								'border-border',
								onRowClick && 'cursor-pointer hover:bg-muted/50 transition-colors',
								getRowClassName?.(row),
							)}
							// Stale rows under the loader can't be opened.
							onClick={onRowClick && !isFetching ? () => onRowClick(row) : undefined}
						>
							{row_getVisibleCells(row).map((cell) => {
								const meta = cell.column.columnDef.meta as DataTableColumnMeta | undefined;
								return (
									<TableCell
										key={cell.id}
										className={cn('p-4', meta?.className)}
										onClick={meta?.stopRowClick ? (event) => event.stopPropagation() : undefined}
									>
										<table.FlexRender cell={cell} />
									</TableCell>
								);
							})}
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}
